// Admin-vindu: inviter brukere inn i systemet ved å utstede enrolment-koder
// (rolle + org-scope), innenfor egen RBAC-scope. Hubben håndhever grensene
// (kan ikke utstede høyere rolle enn egen, ikke kryss-distrikt). Docked + pop-out.
import type { Role } from '@skade/rbac';
import { HV_DISTRICTS, districtAreas, gnaUserIdFromPhone, isMatrixUserId, normalizeMatrixId } from '@skade/contracts';

/** Hjemmeserver-domenet GNÅ-kontoer ligger på (localpart holder i feltet). */
const GNA_DOMENE = 'matrix.follodefence.com';

const ROLES: Array<{ id: Role; label: string }> = [
  { id: 'menig', label: 'Menig' },
  { id: 'lagfører', label: 'Lagfører' },
  { id: 'troppssjef', label: 'Troppssjef' },
  { id: 'områdesjef', label: 'Områdesjef' },
  { id: 'distriktsstab', label: 'Distriktsstab' },
  { id: 'foh', label: 'FOH' },
];
const TTLS: Array<{ label: string; ms: number }> = [
  { label: '24 timer', ms: 24 * 3600_000 },
  { label: '7 dager', ms: 7 * 24 * 3600_000 },
  { label: '30 dager', ms: 30 * 24 * 3600_000 },
];

/** Standard troppsforslag i et tradisjonelt HV-område (jf. hv-structure-malen). */
const TROPP_FORSLAG = ['Stab- og støttetropp', '1. geværtropp', '2. geværtropp', '3. geværtropp', '4. geværtropp'];
/** Standard lagsforslag i en tropp. */
const LAG_FORSLAG = ['Troppsstab', '1. geværlag', '2. geværlag', '3. geværlag', '4. geværlag'];

const CUSTOM = '__annet__';
type Opt = { v: string; t: string };

export interface AdminSelf { id: string; role: string; org: { district?: string; area?: string; troop?: string; team?: string } }

export class AdminPanel {
  readonly el: HTMLElement;
  private open = false;
  private resultEl!: HTMLElement;

  constructor(private self: AdminSelf, private pushHent: (sti: string) => Promise<unknown> = pushDiagnoseHent) {
    this.el = this.build();
  }

  toggle() { this.open = !this.open; this.el.classList.toggle('open', this.open); }

  private build(): HTMLElement {
    const p = div('report-composer admin-panel');
    const head = div('report-head');
    head.append(span('report-title', 'Administrasjon'));
    const x = document.createElement('button'); x.className = 'report-x'; x.textContent = '×'; x.onclick = () => this.toggle();
    head.append(x);
    p.append(head);
    p.append(span('checklist-note', `Innlogget: ${short(this.self.id)} · ${this.self.role} · ${this.self.org.district ?? '—'}${this.self.org.area ? '/' + this.self.org.area : ''}`));

    const form = div('report-form');
    const roleSel = sel(ROLES.map((r) => ({ v: r.id, t: r.label })));
    roleSel.value = 'områdesjef';

    // Dropdowns med forslag fra HV-strukturen + «Annet…» for egendefinerte verdier.
    const districtOpts = HV_DISTRICTS.map((d) => ({ v: d.id, t: `${d.id} · ${d.name}` }));
    const district = new OrgField('Distrikt', districtOpts, this.self.org.district);
    const area = new OrgField('Område', areaOptsFor(this.self.org.district), this.self.org.area);
    const troop = new OrgField('Tropp', TROPP_FORSLAG.map((t) => ({ v: t, t })), this.self.org.troop);
    const team = new OrgField('Lag', LAG_FORSLAG.map((t) => ({ v: t, t })), this.self.org.team);
    // Kaskade: når distrikt endres, oppdater område-forslagene til det distriktet.
    district.onChange((d) => area.setOptions(areaOptsFor(d)));

    // MOTTAKER: nøkkelen utstedes TIL én GNÅ-bruker. Uten adressat er koden en
    // bærer-hemmelighet — videresendes den, arver hvem som helst rollen og
    // org-scopet, og etterpå finnes ingen spor av hvem den var ment for.
    // Feltet er tilgivende: telefonnummer, localpart eller full @id.
    const mottaker = input('Mottaker: telefonnummer eller @gna-id', '');

    const ttlSel = sel(TTLS.map((t, i) => ({ v: String(i), t: t.label })));
    const uses = input('Antall bruk (1–1000)', '1'); (uses.control as HTMLInputElement).type = 'number';

    const btn = document.createElement('button');
    btn.className = 'report-submit'; btn.textContent = 'Utsted enrolment-kode';
    btn.onclick = () => this.issue({
      role: roleSel.value as Role,
      org: {
        district: district.value(),
        area: area.value() || undefined,
        troop: troop.value() || undefined,
        team: team.value() || undefined,
      },
      ttlMs: TTLS[Number(ttlSel.value) || 0].ms,
      maxUses: Math.max(1, Math.min(1000, Number((uses.control as HTMLInputElement).value) || 1)),
      subject: tolkMottaker((mottaker.control as HTMLInputElement).value),
    });
    form.append(labeled('Rolle', roleSel), district.wrap, area.wrap, troop.wrap, team.wrap, mottaker.wrap, labeled('Gyldig', ttlSel), uses.wrap, btn);
    p.append(form);

    this.resultEl = div('admin-result');
    p.append(this.resultEl);

    p.append(this.buildResetSection());
    p.append(this.buildPushDiagnoseSection());
    return p;
  }

  /**
   * PUSH-03 — leser hubbens `GET /push/diagnose` (eierrapport 2026-08-18:
   * «Jeg får fremdeles ikke varslinger på iOS»). Ruta har alltid vært der og
   * fungert — bare tilgjengelig via curl. Egen konto sjekkes alltid; andres
   * konto krever `audit`-lesefullmakt, og UI-en DUPLISERER ALDRI den sjekken —
   * et 403 fra hubben vises rett fram (§4: håndheves autoritativt i backend).
   */
  private buildPushDiagnoseSection(): HTMLElement {
    const section = div('report-form admin-push-section');
    section.append(span('report-title', 'Push-diagnose'));
    section.append(span('checklist-note',
      'Leser Synapse: er varsel-pusheren i det hele tatt registrert for kontoen? '
      + 'Utelukker ett ledd (server), sier ingenting om APNs/Sygnal etter det.'));

    const egenResultEl = div('admin-result push-result');
    const egenBtn = document.createElement('button');
    egenBtn.className = 'report-submit'; egenBtn.textContent = 'Sjekk min push-status';
    egenBtn.onclick = () => this.checkPush(undefined, egenResultEl);
    section.append(egenBtn, egenResultEl);

    // Andres konto — kun for stab med `audit`-lesefullmakt. Ingen RBAC-sjekk
    // her i klienten: kallet får feile ærlig med hubbens egen 403-tekst.
    section.append(span('checklist-item-title', 'Sjekk for denne brukeren (krever fullmakt)'));
    const brukerInput = input('Bruker-id (telefonnummer, @gna-id eller mxid)', '');
    const andreResultEl = div('admin-result push-result');
    const andreBtn = document.createElement('button');
    andreBtn.className = 'report-submit'; andreBtn.textContent = 'Sjekk denne brukeren';
    andreBtn.onclick = () => {
      const raa = (brukerInput.control as HTMLInputElement).value.trim();
      if (!raa) { andreResultEl.innerHTML = ''; andreResultEl.append(span('admin-err', 'Bruker-id kreves.')); return; }
      this.checkPush(raa, andreResultEl);
    };
    section.append(brukerInput.wrap, andreBtn, andreResultEl);

    return section;
  }

  private async checkPush(bruker: string | undefined, resultEl: HTMLElement) {
    resultEl.innerHTML = '';
    resultEl.append(span('checklist-note', 'Henter …'));
    try {
      const sti = bruker ? `/push/diagnose?bruker=${encodeURIComponent(bruker)}` : '/push/diagnose';
      const svar = lesPushDiagnoseSvar(await this.pushHent(sti));
      resultEl.innerHTML = '';
      if (!svar) { resultEl.append(span('admin-err', 'Uventet svarformat fra hubben.')); return; }
      resultEl.append(renderPushDiagnose(svar));
    } catch (e) {
      // ⚠ EN AVVIST FORESPØRSEL (403 fra hubben pga. manglende `audit`-fullmakt,
      // eller et annet feilsvar) SKAL VISES SOM DET DEN ER — aldri stille, og
      // aldri som en falsk «0 pushere». Se PUSH-03-kravet.
      resultEl.innerHTML = '';
      resultEl.append(span('admin-err', feilTekst(e)));
    }
  }

  /**
   * QA-018 — selvbetjent passord-reset for en EKSISTERENDE bruker. Samme
   * RBAC-nivå og samme «utsted engangs-kode»-mønster som enrolment over
   * (hubben håndhever autoritativt: lagfører+, ikke høyere rolle enn egen,
   * ikke utenfor egen scope) — men denne koden endrer ALDRI org/rolle, kun
   * passordet på kontoen som allerede finnes. Se `login-view.ts` for
   * innløsningsskjemaet brukeren selv fyller ut.
   */
  private buildResetSection(): HTMLElement {
    const section = div('report-form admin-reset-section');
    section.append(span('report-title', 'Passord-reset (eksisterende bruker)'));
    section.append(span('checklist-note',
      'Til bruk når noen er låst ute. Brukeren løser inn koden selv i innloggingsskjermen — org og rolle røres ikke.'));

    const userId = input('Bruker-id (den som er låst ute)', '');
    const ttlSel = sel(TTLS.map((t, i) => ({ v: String(i), t: t.label })));
    ttlSel.value = '0'; // 24 timer, samme standard som enrolment

    const resultEl = div('admin-result');
    const btn = document.createElement('button');
    btn.className = 'report-submit'; btn.textContent = 'Utsted reset-kode';
    btn.onclick = () => this.issueReset((userId.control as HTMLInputElement).value.trim(), TTLS[Number(ttlSel.value) || 0].ms, resultEl);

    section.append(userId.wrap, labeled('Gyldig', ttlSel), btn, resultEl);
    return section;
  }

  private async issueReset(userId: string, ttlMs: number, resultEl: HTMLElement) {
    resultEl.innerHTML = '';
    if (!userId) { resultEl.append(span('admin-err', 'Bruker-id kreves.')); return; }
    try {
      const r = await fetch('/admin/passord-reset-token', {
        method: 'POST', headers: { 'content-type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ userId, ttlMs }),
      });
      const j = await r.json() as { code?: string; userId?: string; error?: string; expiresAt?: number };
      if (!r.ok || !j.code) { resultEl.append(span('admin-err', j.error ? `Avvist: ${j.error}` : `Feil (${r.status})`)); return; }
      this.showResetCode(resultEl, j.code, j.userId ?? userId, j.expiresAt);
    } catch { resultEl.append(span('admin-err', 'Nettverksfeil (krever innlogget backend).')); }
  }

  private showResetCode(resultEl: HTMLElement, code: string, userId: string, expiresAt?: number) {
    resultEl.innerHTML = '';
    const box = div('admin-code-box');
    box.append(span('checklist-note',
      `Reset-kode for ${userId}${expiresAt ? ` (utløper ${new Date(expiresAt).toLocaleString('nb-NO')})` : ''}:`));
    const codeEl = document.createElement('div'); codeEl.className = 'admin-code'; codeEl.textContent = code;
    const copy = document.createElement('button'); copy.className = 'targetlist-btn'; copy.textContent = 'Kopier';
    copy.onclick = () => { navigator.clipboard?.writeText(code).then(() => { copy.textContent = 'Kopiert ✓'; }, () => {}); };
    box.append(codeEl, copy, span('checklist-note',
      `Koden er bundet til «${userId}» og virker bare for den kontoen, én gang. `
      + 'Gi den til brukeren muntlig/skriftlig — de løser den inn i innloggingsskjermen under «Glemt passord».'));
    resultEl.append(box);
  }

  private async issue(body: {
    role: Role;
    org: Record<string, string | undefined>;
    ttlMs: number;
    maxUses: number;
    subject: string;
  }) {
    this.resultEl.innerHTML = '';
    if (!body.org.district) { this.resultEl.append(span('admin-err', 'Distrikt kreves.')); return; }
    // Sjekkes HER også, ikke bare på serveren: en tom mottaker skal gi en
    // forklaring operatøren forstår, ikke en 400 fra et endepunkt.
    if (!body.subject) {
      this.resultEl.append(span('admin-err',
        'Mottaker kreves — skriv telefonnummeret eller @gna-id-en nøkkelen skal utstedes til. '
        + 'En kode uten mottaker kan videresendes og brukes av andre.'));
      return;
    }
    try {
      const r = await fetch('/admin/enrol-token', {
        method: 'POST', headers: { 'content-type': 'application/json' }, credentials: 'include', body: JSON.stringify(body),
      });
      const j = await r.json() as { code?: string; error?: string; expiresAt?: number; maxUses?: number };
      if (!r.ok || !j.code) { this.resultEl.append(span('admin-err', j.error ? `Avvist: ${j.error}` : `Feil (${r.status})`)); return; }
      this.showCode(j.code, body.role, j.expiresAt, j.maxUses);
    } catch { this.resultEl.append(span('admin-err', 'Nettverksfeil (krever innlogget backend).')); }
  }

  private showCode(code: string, role: Role, expiresAt?: number, maxUses?: number) {
    this.resultEl.innerHTML = '';
    const box = div('admin-code-box');
    box.append(span('checklist-note', `Enrolment-kode (${role}${maxUses ? `, ${maxUses} bruk` : ''}${expiresAt ? `, utløper ${new Date(expiresAt).toLocaleString('nb-NO')}` : ''}):`));
    const codeEl = document.createElement('div'); codeEl.className = 'admin-code'; codeEl.textContent = code;
    const copy = document.createElement('button'); copy.className = 'targetlist-btn'; copy.textContent = 'Kopier';
    copy.onclick = () => { navigator.clipboard?.writeText(code).then(() => { copy.textContent = 'Kopiert ✓'; }, () => {}); };
    box.append(codeEl, copy, span('checklist-note',
      'Koden er bundet til mottakeren og kan bare løses inn av den kontoen. '
      + 'Mottaker: COP → «Registrer enhet» → lim inn.'));
    this.resultEl.append(box);
  }
}

/**
 * Tolker mottakerfeltet tilgivende: «91994407» → «@gna4791994407:…»,
 * «gna4791994407» → full id, og en ferdig «@…:…» slippes gjennom. Tom streng
 * når feltet ikke gir en gyldig Matrix-id (kalleren nekter da å sende).
 */
function tolkMottaker(raa: string): string {
  const t = raa.trim();
  if (!t) return '';
  const fraNummer = gnaUserIdFromPhone(t, GNA_DOMENE);
  if (fraNummer) return fraNummer;
  const normalisert = normalizeMatrixId(t, GNA_DOMENE);
  return normalisert && isMatrixUserId(normalisert) ? normalisert : '';
}

/**
 * Én push-registrering, slik hubben oppgir den (`PusherFunn` i
 * `services/hub/src/pushdiagnose.ts`). Pushkey er allerede maskert av hubben
 * FØR den kommer over wire — klienten skal aldri se hele tokenet.
 */
interface PushRegistrering {
  appId: string; enhet: string; app: string; pushkey: string; registrert: string; format: string; gateway: string;
}
interface PushDiagnoseSvar {
  bruker: string;
  antall: number;
  pushere: PushRegistrering[];
  konklusjon: string;
  forbehold: string;
}

/** Les `GET /push/diagnose`-svaret defensivt. Et svar uten `konklusjon`/
 * `forbehold` regnes som uforståelig, ikke som «0 pushere». */
function lesPushDiagnoseSvar(u: unknown): PushDiagnoseSvar | null {
  const o = u as Record<string, unknown> | null;
  if (!o || typeof o.konklusjon !== 'string' || typeof o.forbehold !== 'string' || typeof o.antall !== 'number') return null;
  const raaPushere = Array.isArray(o.pushere) ? o.pushere : [];
  const pushere: PushRegistrering[] = raaPushere.map((p) => {
    const r = p as Record<string, unknown>;
    return {
      appId: typeof r.appId === 'string' ? r.appId : '(ukjent)',
      enhet: typeof r.enhet === 'string' ? r.enhet : '',
      app: typeof r.app === 'string' ? r.app : '',
      pushkey: typeof r.pushkey === 'string' ? r.pushkey : '',
      registrert: typeof r.registrert === 'string' ? r.registrert : '',
      format: typeof r.format === 'string' ? r.format : '',
      gateway: typeof r.gateway === 'string' ? r.gateway : '',
    };
  });
  return {
    bruker: typeof o.bruker === 'string' ? o.bruker : '',
    antall: o.antall,
    pushere,
    konklusjon: o.konklusjon,
    forbehold: o.forbehold,
  };
}

/** Bygg en lesbar visning — konklusjon og forbehold TYDELIG, ikke rå JSON. */
function renderPushDiagnose(d: PushDiagnoseSvar): HTMLElement {
  const box = div('admin-code-box push-diagnose');
  box.append(span('checklist-item-title', `${short(d.bruker)} — ${d.antall} pusher(e) registrert`));
  const konklusjon = div('push-konklusjon'); konklusjon.textContent = d.konklusjon;
  box.append(konklusjon);
  const forbehold = div('checklist-note push-forbehold'); forbehold.textContent = d.forbehold;
  box.append(forbehold);
  if (d.pushere.length) {
    const liste = div('push-liste');
    for (const p of d.pushere) {
      const rad = div('push-rad');
      rad.append(span('push-rad-app', `${p.app || p.appId}${p.enhet ? ` · ${p.enhet}` : ''}`));
      rad.append(span('checklist-note',
        `format: ${p.format || '(ukjent)'} · gateway: ${p.gateway || '(mangler)'}`
        + `${p.registrert ? ` · registrert: ${p.registrert}` : ''}`));
      liste.append(rad);
    }
    box.append(liste);
  }
  return box;
}

/** Autentisert GET mot hubben — samme mønster som `personregisterHent`/
 * `kapabilitetHent`: sesjons-cookie, og et feilsvar kaster med hubbens EGEN
 * feiltekst (inkl. et 403 for manglende `audit`-fullmakt), aldri stille. */
export async function pushDiagnoseHent(sti: string): Promise<unknown> {
  const r = await fetch(sti, { credentials: 'include' });
  if (!r.ok) {
    let feil = `${r.status} ${r.statusText || 'feil'}`;
    try {
      const j = (await r.json()) as { error?: unknown };
      if (typeof j?.error === 'string') feil = `${r.status} ${j.error}`;
    } catch { /* ikke JSON — statuskoden er alt vi har */ }
    throw new Error(feil);
  }
  return r.json();
}

function feilTekst(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
function short(id: string): string { return id.replace(/^@/, '').replace(/:.*$/, ''); }
function sel(opts: { v: string; t: string }[]): HTMLSelectElement {
  const s = document.createElement('select'); s.className = 'checklist-sel';
  for (const o of opts) { const opt = document.createElement('option'); opt.value = o.v; opt.textContent = o.t; s.append(opt); }
  return s;
}
function input(placeholder: string, value: string): { wrap: HTMLElement; control: HTMLInputElement } {
  const control = document.createElement('input'); control.className = 'report-input'; control.placeholder = placeholder; control.value = value;
  return { wrap: labeled(placeholder, control), control };
}
function labeled(label: string, control: HTMLElement): HTMLElement {
  const wrap = div('report-field'); wrap.append(span('report-field-label', label), control); return wrap;
}

/** Områdeforslag for et distrikt (id + navn), til område-dropdownen. */
function areaOptsFor(district?: string): Opt[] {
  const d = HV_DISTRICTS.find((x) => x.id === district);
  if (!d) return [];
  return districtAreas(d).map((a) => ({ v: a.id, t: `${a.number} · ${a.name}` }));
}

/**
 * Dropdown med forslag + «Annet…» som avslører et fritekstfelt for egendefinerte
 * verdier (Områder/Tropper/Lag distriktet selv oppretter). value() gir enten
 * valgt forslag eller den egendefinerte teksten. Forhåndsvelger `preset` (matcher
 * enten en opsjonsverdi eller settes som egendefinert tekst).
 */
class OrgField {
  readonly wrap: HTMLElement;
  private sel: HTMLSelectElement;
  private txt: HTMLInputElement;
  private cb?: (v: string) => void;

  constructor(label: string, opts: Opt[], preset?: string) {
    this.sel = document.createElement('select'); this.sel.className = 'checklist-sel';
    this.txt = document.createElement('input'); this.txt.className = 'report-input'; this.txt.placeholder = 'Egendefinert …'; this.txt.style.display = 'none'; this.txt.style.marginTop = '4px';
    this.fill(opts, preset);
    this.sel.onchange = () => { this.syncCustom(); this.cb?.(this.value()); };
    const box = div('org-field-box'); box.append(this.sel, this.txt);
    this.wrap = labeled(label, box);
  }

  onChange(cb: (v: string) => void) { this.cb = cb; }

  /** Bytt forslagslista (kaskade), behold egendefinert/valgt verdi der mulig. */
  setOptions(opts: Opt[]) {
    const prev = this.value();
    this.fill(opts, prev);
    this.syncCustom();
  }

  private fill(opts: Opt[], preset?: string) {
    this.sel.innerHTML = '';
    const blank = document.createElement('option'); blank.value = ''; blank.textContent = opts.length ? '— velg —' : '(ingen forslag)'; this.sel.append(blank);
    for (const o of opts) { const opt = document.createElement('option'); opt.value = o.v; opt.textContent = o.t; this.sel.append(opt); }
    const custom = document.createElement('option'); custom.value = CUSTOM; custom.textContent = 'Annet … (egendefinert)'; this.sel.append(custom);
    // Forhåndsvelg: matcher opsjon → velg den; ellers behandle som egendefinert.
    if (preset && opts.some((o) => o.v === preset)) { this.sel.value = preset; this.txt.value = ''; }
    else if (preset) { this.sel.value = CUSTOM; this.txt.value = preset; }
    else { this.sel.value = ''; }
    this.syncCustom();
  }

  private syncCustom() { this.txt.style.display = this.sel.value === CUSTOM ? '' : 'none'; }

  value(): string { return this.sel.value === CUSTOM ? this.txt.value.trim() : this.sel.value; }
}
